import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { accountsAPI, chatbotsAPI } from '@/services/api';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { HelpButton } from '@/components/common/HelpButton';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Bot, 
  MessageSquare, 
  FileText, 
  Plus,
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  AlertTriangle,
  Zap,
  X,
  Calendar,
  Users,
  TrendingUp,
  Clock,
  Activity,
  Globe,
  Share2,
  BarChart3,
  Gift,
  DollarSign,
  Target,
  UserCheck,
  Trophy,
  PieChart,
  MessageCircle,
  Facebook,
  Instagram,
  Linkedin
} from 'lucide-react';

export const DashboardOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    chatbots: 0,
    documents: 0,
    conversations: 0,
    totalMessages: 0
  });
  const [recentChatbots, setRecentChatbots] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(null);
  const [showCreditWarning, setShowCreditWarning] = useState(true);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [leadStats, setLeadStats] = useState(null);
  const [dateFilter, setDateFilter] = useState('30days');
  const [dailyTrend, setDailyTrend] = useState([]);

  useEffect(() => {
    loadDashboardData();
    loadCredits();
    loadLeadStats();
  }, [dateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboardData = async () => {
    try {
      const rangeMap = { '7days': '7d', '30days': '30d', '90days': '90d' };
      const range = rangeMap[dateFilter] || '30d';
      
      const [chatbotsRes, analyticsRes] = await Promise.all([
        chatbotsAPI.listAll(),
        api.get(`/analytics/overview?range=${range}`).catch(() => ({ data: {} }))
      ]);

      const chatbots = chatbotsRes.data || [];
      const analytics = analyticsRes.data || {};

      setStats({
        chatbots: chatbots.length,
        documents: analytics.total_documents || 0,
        conversations: analytics.total_conversations || 0,
        totalMessages: analytics.total_messages || 0
      });
      
      setDailyTrend(analytics.daily || []);

      setRecentChatbots(chatbots.slice(0, 4));
      
      // Generate recent activity from chatbots
      const activities = chatbots.slice(0, 5).map(bot => ({
        id: bot.chatbot_id,
        type: 'chatbot',
        title: bot.name,
        action: bot.status === 'active' ? 'Active' : 'Updated',
        time: bot.updated_at || bot.created_at,
        icon: Bot
      }));
      setRecentActivity(activities);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeadStats = async () => {
    try {
      const response = await api.get('/leads/dashboard/stats');
      setLeadStats(response.data);
    } catch (error) {
      console.error('Error loading lead stats:', error);
    }
  };

  const loadCredits = async () => {
    try {
      const response = await api.get('/notifications/credits');
      setCredits(response.data);
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'draft': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'paused': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  // Inline sparkline for stat cards
  const Sparkline = ({ data, dataKey, color = '#3b82f6' }) => {
    if (!data || data.length < 2) return null;
    const values = data.map(d => d[dataKey] || 0);
    const max = Math.max(...values, 1);
    const min = Math.min(...values);
    const range = max - min || 1;
    const w = 80; const h = 24;
    const points = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / range) * (h - 4) - 2}`).join(' ');
    return (
      <svg width={w} height={h} className="inline-block ml-2 opacity-70">
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };


  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days > 0) return `${days}d ago`;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours > 0) return `${hours}h ago`;
    const minutes = Math.floor(diff / (1000 * 60));
    return minutes > 0 ? `${minutes}m ago` : 'Just now';
  };

  return (
    <div className="space-y-6" data-testid="my-dashboard">
      {/* Low Credit Warning Banner */}
      {credits?.is_low_credits && showCreditWarning && (
        <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-900">Usage Alert</p>
              <p className="text-sm text-amber-700">{credits.low_credits_warning}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard/settings?tab=billing">
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm">
                Upgrade Plan
              </Button>
            </Link>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => setShowCreditWarning(false)}
              className="text-amber-600 hover:bg-amber-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Welcome Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
            {getGreeting()}, {user?.name?.split(' ')[0]}! 👋
          </h1>
          <p className="mt-1 text-slate-600">Here&apos;s an overview of your AI employee platform.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-[140px]" data-testid="dashboard-date-filter">
              <Calendar className="w-4 h-4 mr-1.5 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="90days">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <HelpButton 
            title="Dashboard Overview"
            content={`
              <p>Your command center for managing AI agents.</p>
              <ul>
                <li><strong>Stats:</strong> Key metrics at a glance</li>
                <li><strong>AI Employees:</strong> Your AI employees</li>
                <li><strong>Usage:</strong> Track your plan limits</li>
              </ul>
            `}
          />
          <Button 
            onClick={() => navigate('/dashboard/chatbots?new=1')}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/20 w-full sm:w-auto" 
            data-testid="create-chatbot-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New AI Employee
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Active AI Employees</p>
                <p className="text-3xl font-bold text-slate-900">{stats.chatbots}</p>
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Ready to engage
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Bot className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Conversations</p>
                <div className="flex items-end gap-1">
                  <p className="text-3xl font-bold text-slate-900">{stats.conversations.toLocaleString()}</p>
                  <Sparkline data={dailyTrend} dataKey="conversations" color="#10b981" />
                </div>
                <p className="text-xs text-slate-500 mt-1">This month</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Knowledge Docs</p>
                <p className="text-3xl font-bold text-slate-900">{stats.documents}</p>
                <p className="text-xs text-slate-500 mt-1">Uploaded files</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Messages</p>
                <div className="flex items-end gap-1">
                  <p className="text-3xl font-bold text-slate-900">{stats.totalMessages.toLocaleString()}</p>
                  <Sparkline data={dailyTrend} dataKey="messages" color="#f59e0b" />
                </div>
                <p className="text-xs text-slate-500 mt-1">AI responses sent</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Zap className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CRM Metrics Section */}
      {leadStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium mb-1">Total Leads</p>
                  <p className="text-3xl font-bold text-slate-900">{leadStats.total_leads?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">All captured leads</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600 font-medium mb-1">Qualified Leads</p>
                  <p className="text-3xl font-bold text-slate-900">{leadStats.qualified_leads?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Ready for outreach</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <UserCheck className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium mb-1">Conversions</p>
                  <p className="text-3xl font-bold text-slate-900">{leadStats.converted_leads?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Closed deals</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-100 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium mb-1">Conversion Rate</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {leadStats.total_leads > 0 
                      ? ((leadStats.converted_leads / leadStats.total_leads) * 100).toFixed(1) 
                      : 0}%
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Lead to customer</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <Target className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Lead Pipeline Mini Chart */}
      {leadStats?.stage_breakdown && Object.keys(leadStats.stage_breakdown).length > 0 && (
        <Card className="mb-8 bg-white border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <PieChart className="w-5 h-5 text-indigo-600" />
                </div>
                <CardTitle className="text-slate-900">Lead Pipeline Overview</CardTitle>
              </div>
              <Link to="/dashboard/leads" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                View Pipeline <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { key: 'raw', label: 'Raw', color: 'bg-slate-500' },
                { key: 'contacted', label: 'Contacted', color: 'bg-blue-500' },
                { key: 'qualified', label: 'Qualified', color: 'bg-emerald-500' },
                { key: 'proposal', label: 'Proposal', color: 'bg-amber-500' },
                { key: 'converted', label: 'Converted', color: 'bg-green-600' }
              ].map(stage => (
                <div key={stage.key} className="text-center p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div className={`w-3 h-3 rounded-full ${stage.color} mx-auto mb-2`}></div>
                  <p className="text-2xl font-bold text-slate-900">
                    {leadStats.stage_breakdown[stage.key] || 0}
                  </p>
                  <p className="text-sm text-slate-600">{stage.label}</p>
                </div>
              ))}
            </div>
            
            {/* Progress Bar */}
            <div className="mt-6">
              <p className="text-sm text-slate-600 mb-2">Pipeline Progress</p>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                {leadStats.total_leads > 0 && (
                  <>
                    <div 
                      className="h-full bg-slate-400 transition-all" 
                      style={{ width: `${((leadStats.stage_breakdown.raw || 0) / leadStats.total_leads) * 100}%` }}
                    />
                    <div 
                      className="h-full bg-blue-500 transition-all" 
                      style={{ width: `${((leadStats.stage_breakdown.contacted || 0) / leadStats.total_leads) * 100}%` }}
                    />
                    <div 
                      className="h-full bg-emerald-500 transition-all" 
                      style={{ width: `${((leadStats.stage_breakdown.qualified || 0) / leadStats.total_leads) * 100}%` }}
                    />
                    <div 
                      className="h-full bg-amber-500 transition-all" 
                      style={{ width: `${((leadStats.stage_breakdown.proposal || 0) / leadStats.total_leads) * 100}%` }}
                    />
                    <div 
                      className="h-full bg-green-600 transition-all" 
                      style={{ width: `${((leadStats.stage_breakdown.converted || 0) / leadStats.total_leads) * 100}%` }}
                    />
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* AI Employees Section - Takes 2 columns on large screens */}
        <div className="md:col-span-2 lg:col-span-2 space-y-6">
          {/* Your AI Employees */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Bot className="w-5 h-5 text-blue-600" />
                  </div>
                  <CardTitle className="text-slate-900">Your AI Employees</CardTitle>
                </div>
                <Link to="/dashboard/chatbots" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-slate-500">Loading...</div>
              ) : recentChatbots.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-4">
                    <Bot className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-slate-600 mb-4">No AI agents yet. Create your first AI assistant!</p>
                  <Button onClick={() => navigate('/dashboard/chatbots')} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create AI Employee
                  </Button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4 p-4">
                  {recentChatbots.map((chatbot) => (
                    <Link 
                      key={chatbot.chatbot_id}
                      to={`/dashboard/chatbots/${chatbot.chatbot_id}`}
                      className="group p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all bg-gradient-to-br from-white to-slate-50"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                          <Bot className="w-5 h-5 text-white" />
                        </div>
                        <Badge className={getStatusColor(chatbot.status)}>
                          {chatbot.status}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors mb-1">
                        {chatbot.name}
                      </h3>
                      <p className="text-sm text-slate-500 line-clamp-1">
                        {chatbot.description || 'No description'}
                      </p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(chatbot.updated_at)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 hover:border-blue-300 transition-all cursor-pointer group" onClick={() => navigate('/dashboard/chatbots')}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Sparkles className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">Create AI Assistant</h3>
                    <p className="text-sm text-slate-600">Build AI agents powered by your documents</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 hover:border-purple-300 transition-all cursor-pointer group" onClick={() => {
              // Smart navigation: if the user already has at least one bot,
              // deep-link straight into that bot's Channels tab so they can
              // wire up WhatsApp/Facebook/Instagram immediately. Otherwise
              // send them to the chatbots list with a hint to create one.
              if (recentChatbots && recentChatbots.length > 0) {
                navigate(`/dashboard/chatbots/${recentChatbots[0].chatbot_id}?tab=channels`);
              } else {
                toast.info('Create your first AI Employee to connect channels.');
                navigate('/dashboard/chatbots');
              }
            }} data-testid="quick-action-connect-channels">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Share2 className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">Connect Channels</h3>
                    <p className="text-sm text-slate-600">WhatsApp, Facebook, Instagram & more</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Usage & Credits */}
          {credits && (
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Zap className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-slate-900 text-base">Usage & Credits</CardTitle>
                    <p className="text-xs text-slate-500 capitalize">{(credits.tier || '').replace(/_/g, ' ')} Plan</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* Messages */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Messages</span>
                    <span className="text-slate-900 font-medium">
                      {credits.message_limit === -1 ? '∞' : `${credits.messages_used} / ${credits.message_limit}`}
                    </span>
                  </div>
                  {credits.message_limit !== -1 && (
                    <Progress value={credits.messages_percent_used} className="h-2" />
                  )}
                  {credits.message_limit === -1 && (
                    <p className="text-xs text-emerald-600">✓ Unlimited</p>
                  )}
                </div>

                {/* Documents */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Documents</span>
                    <span className="text-slate-900 font-medium">
                      {credits.document_limit === -1 ? '∞' : `${credits.documents_used} / ${credits.document_limit}`}
                    </span>
                  </div>
                  {credits.document_limit !== -1 && (
                    <Progress value={credits.documents_percent_used} className="h-2" />
                  )}
                  {credits.document_limit === -1 && (
                    <p className="text-xs text-emerald-600">✓ Unlimited</p>
                  )}
                </div>

                {/* AI Employees */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">AI Employees</span>
                    <span className="text-slate-900 font-medium">
                      {credits.chatbot_limit === -1 ? '∞' : `${credits.chatbots_used} / ${credits.chatbot_limit}`}
                    </span>
                  </div>
                  {credits.chatbot_limit !== -1 && (
                    <Progress value={(credits.chatbots_used / credits.chatbot_limit) * 100} className="h-2" />
                  )}
                  {credits.chatbot_limit === -1 && (
                    <p className="text-xs text-emerald-600">✓ Unlimited</p>
                  )}
                </div>

                <Link to="/dashboard/settings?tab=billing">
                  <Button variant="outline" className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 mt-2">
                    Manage Plan
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Recent Activity */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Activity className="w-5 h-5 text-emerald-600" />
                </div>
                <CardTitle className="text-slate-900 text-base">Recent Activity</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {recentActivity.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  No recent activity
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {recentActivity.map((activity, index) => (
                    <div
                      key={activity.id || `${activity.title}-${activity.time || index}`}
                      className="px-4 py-3 flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <activity.icon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 truncate">{activity.title}</p>
                        <p className="text-xs text-slate-500">{activity.action}</p>
                      </div>
                      <span className="text-xs text-slate-400">{formatTimeAgo(activity.time)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Integrations Preview */}
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-0 shadow-lg" data-testid="expand-your-reach-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/15 rounded-lg ring-1 ring-white/20">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white drop-shadow-sm">Expand Your Reach</h3>
              </div>
              <p className="text-sm text-slate-100 mb-4 leading-relaxed">
                Connect your AI agents to WhatsApp, Facebook, Instagram, and LinkedIn.
              </p>
              <div className="flex gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#25D366]/30 ring-1 ring-white/10 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-white" />
                </div>
                <div className="w-8 h-8 rounded-lg bg-[#1877F2]/30 ring-1 ring-white/10 flex items-center justify-center">
                  <Facebook className="w-4 h-4 text-white" />
                </div>
                <div className="w-8 h-8 rounded-lg bg-[#E4405F]/30 ring-1 ring-white/10 flex items-center justify-center">
                  <Instagram className="w-4 h-4 text-white" />
                </div>
                <div className="w-8 h-8 rounded-lg bg-[#0A66C2]/30 ring-1 ring-white/10 flex items-center justify-center">
                  <Linkedin className="w-4 h-4 text-white" />
                </div>
              </div>
              <Button
                onClick={() => {
                  if (chatbots.length > 0) {
                    navigate(`/dashboard/chatbots/${chatbots[0].chatbot_id}?tab=channels`);
                  } else {
                    toast.info('Create your first AI Employee to connect channels.');
                    navigate('/dashboard/chatbots');
                  }
                }}
                className="w-full bg-white text-slate-900 hover:bg-slate-100 font-semibold shadow-md"
                data-testid="expand-reach-connect-btn"
              >
                Connect Channels
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
